import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  KpiCard,
  NativeSelect,
  NoPermissionState,
  PageHeader,
  SegmentedSwitch,
  TableSkeleton,
  Toolbar,
  toast,
  usePrint,
} from '@stayafrica/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, notFound } from '@tanstack/react-router';
import { BarChart3, Download, Printer } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  downloadReportCsv,
  reportQueryOptions,
  type ReportGroupBy,
  type ReportScope,
} from '@/features/analytics/queries';
import {
  PERIOD_PRESETS,
  resolvePeriod,
  todayIsoDate,
  type PeriodPreset,
  type PeriodRange,
} from '@/features/analytics/period';
import { PeriodPicker } from '@/features/analytics/PeriodPicker';
import { useSession } from '@/features/auth/session';
import { ReportTable } from '@/features/reports/ReportTable';
import { canOpenReport, reportBySlug } from '@/features/reports/catalog';
import { openDownloadedFile } from '@/lib/api';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatNumber } from '@/lib/format';

interface ReportSearch {
  period?: PeriodPreset;
  from?: string;
  to?: string;
  groupBy?: ReportGroupBy;
  status?: string;
  scope?: ReportScope;
}

const REPORT_SCOPES: ReportScope[] = ['ENGAGED', 'ALL', 'CANCELLED'];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export const Route = createFileRoute('/_app/reports/$kind')({
  validateSearch: (search: Record<string, unknown>): ReportSearch => ({
    ...(PERIOD_PRESETS.includes(search['period'] as PeriodPreset)
      ? { period: search['period'] as PeriodPreset }
      : {}),
    ...(typeof search['from'] === 'string' && ISO_DATE.test(search['from'])
      ? { from: search['from'] }
      : {}),
    ...(typeof search['to'] === 'string' && ISO_DATE.test(search['to'])
      ? { to: search['to'] }
      : {}),
    ...(typeof search['groupBy'] === 'string'
      ? { groupBy: search['groupBy'] as ReportGroupBy }
      : {}),
    ...(typeof search['status'] === 'string' ? { status: search['status'] } : {}),
    ...(REPORT_SCOPES.includes(search['scope'] as ReportScope)
      ? { scope: search['scope'] as ReportScope }
      : {}),
  }),
  beforeLoad: ({ params }) => {
    // Un `kind` inconnu ne doit pas produire un appel API : 404 immédiat.
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router utilise une exception
    if (!reportBySlug(params.kind)) throw notFound();
  },
  component: ReportPage,
});

/**
 * Page d'un rapport (SCR-101 à SCR-104) : filtres (période, regroupement,
 * statut), KPI de tête, tableau dense trié avec totaux, export CSV
 * (`report.export`) et impression.
 */
function ReportPage() {
  const { t, i18n } = useTranslation([
    'reports',
    'analytics',
    'reservations',
    'billing',
    'payments',
    'common',
    'errors',
  ]);
  const { kind } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const session = useSession();
  const print = usePrint();
  const [exporting, setExporting] = useState(false);

  const definition = reportBySlug(kind);
  // Même règle que `GET /reports/:kind` : `report.operational` ouvre la route,
  // `report.financial` est exigée en plus pour un rapport financier.
  const canRead = definition
    ? canOpenReport(definition, (permission) => session.can(permission))
    : false;
  // L'export exige la lecture du rapport (`report.operational`, + `report.financial`
  // pour un rapport financier) en plus de `report.export`.
  const canExport = canRead && session.can('report.export');

  const today = todayIsoDate(session.user.tenant.timezone);
  const preset: PeriodPreset = search.period ?? 'THIS_MONTH';
  const custom: PeriodRange = { from: search.from ?? today, to: search.to ?? today };
  const range = resolvePeriod(preset, today, custom);
  // Un regroupement absent de l'URL, ou que ce rapport n'honore pas, retombe
  // sur le premier proposé ; un rapport sans regroupement n'en envoie aucun.
  const groupByOptions = definition?.groupBy ?? [];
  const groupBy =
    search.groupBy && groupByOptions.includes(search.groupBy) ? search.groupBy : groupByOptions[0];
  // Seul le serveur décide quels rapports filtrent par statut (RESERVATIONS).
  const status =
    search.status && definition && definition.statuses.includes(search.status)
      ? search.status
      : undefined;

  // Périmètre (D-053) : « Engagées » par défaut ; seul RESERVATIONS l'honore.
  const scopeOptions = definition?.scopes ?? [];
  const scope =
    search.scope && scopeOptions.includes(search.scope) ? search.scope : scopeOptions[0];

  const filters = {
    from: range.from,
    to: range.to,
    ...(groupBy ? { groupBy } : {}),
    ...(status ? { status } : {}),
    ...(scope ? { scope } : {}),
  };

  const query = useQuery(
    reportQueryOptions(definition?.kind ?? 'REVENUE', filters, Boolean(definition) && canRead),
  );

  const setSearch = (next: Partial<ReportSearch>) =>
    void navigate({ search: (current) => ({ ...current, ...next }), replace: true });

  if (!definition) return null;

  const title = t(`reports:kinds.${definition.kind}.title`);

  if (!canRead) {
    return (
      <Container>
        <PageHeader title={title} subtitle={t(`reports:kinds.${definition.kind}.description`)} />
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const result = query.data;
  const totals = result?.totals ?? null;

  const handleExport = async () => {
    setExporting(true);
    try {
      const file = await downloadReportCsv(definition.kind, filters);
      openDownloadedFile(file);
      toast.success(t('reports:toasts.exported', { name: file.filename }));
    } catch (error) {
      toast.error(errorMessage(error, t));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container>
      <PageHeader
        title={title}
        subtitle={t(`reports:kinds.${definition.kind}.description`)}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('reports:title'), link: <Link to="/reports">{t('reports:title')}</Link> },
          { label: title },
        ]}
        actions={
          <div className="row row--wrap no-print" style={{ gap: 8 }}>
            <Button variant="secondary" onClick={print}>
              <Printer className="ico" aria-hidden="true" />
              {t('reports:actions.print')}
            </Button>
            {canExport ? (
              <Button
                variant="primary"
                loading={exporting}
                disabled={!result || result.rowCount === 0}
                onClick={() => void handleExport()}
              >
                <Download className="ico" aria-hidden="true" />
                {t('reports:actions.exportCsv')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="stack">
        <Card>
          <CardHeader
            title={t('reports:filtersTitle')}
            icon={<BarChart3 className="ico ico-16" aria-hidden="true" />}
            iconTone="blue"
          />
          <CardBody>
            <div className="stack stack--sm">
              <PeriodPicker
                preset={preset}
                onPresetChange={(value) => setSearch({ period: value })}
                range={range}
                custom={custom}
                onCustomChange={(value) => setSearch({ from: value.from, to: value.to })}
                hideCompare
              />
              {scopeOptions.length > 0 && scope ? (
                <div className="row row--wrap no-print" style={{ gap: 12, alignItems: 'center' }}>
                  <span className="t-13 muted">{t('reports:scope.label')}</span>
                  <SegmentedSwitch<ReportScope>
                    label={t('reports:scope.label')}
                    value={scope}
                    onValueChange={(value) => setSearch({ scope: value })}
                    options={scopeOptions.map((value) => ({
                      value,
                      label: t(`reports:scope.${value}`),
                    }))}
                  />
                  <span className="t-12 muted">{t('reports:scope.hint')}</span>
                </div>
              ) : null}
              {groupByOptions.length > 0 || definition.statuses.length > 0 ? (
                <Toolbar className="no-print">
                  {groupByOptions.length > 0 ? (
                    <label className="row" style={{ gap: 8, alignItems: 'center' }}>
                      <span className="t-13 muted">{t('reports:groupBy')}</span>
                      <NativeSelect
                        value={groupBy ?? ''}
                        onChange={(event) =>
                          setSearch({ groupBy: event.target.value as ReportGroupBy })
                        }
                        style={{ width: 200, height: 32, fontSize: 'var(--fs-13)' }}
                      >
                        {groupByOptions.map((value) => (
                          <option key={value} value={value}>
                            {t(`reports:groupByOptions.${value}`)}
                          </option>
                        ))}
                      </NativeSelect>
                    </label>
                  ) : null}
                  {definition.statuses.length > 0 ? (
                    <>
                      <span className="t-13 muted">{t('reports:statusFilter')}</span>
                      {definition.statuses.map((value) => (
                        <ChipFilter
                          key={value}
                          active={status === value}
                          onClick={() =>
                            setSearch({ status: status === value ? undefined : value })
                          }
                        >
                          {definition.statusNamespace
                            ? t(`${definition.statusNamespace}:status.${value}`, {
                                defaultValue: t(`reports:status.${value}`, {
                                  defaultValue: value,
                                }),
                              })
                            : value}
                        </ChipFilter>
                      ))}
                    </>
                  ) : null}
                </Toolbar>
              ) : null}
            </div>
          </CardBody>
        </Card>

        {definition.showTotalsHeader && totals ? (
          <div className="grid grid-4">
            {Object.entries(totals)
              .slice(0, 4)
              .map(([key, value]) => {
                const column = result?.columns.find((entry) => entry.key === key);
                return (
                  <KpiCard
                    key={key}
                    tone={column?.kind === 'money' ? 'green' : 'violet'}
                    label={column?.label ?? key}
                    value={
                      column?.kind === 'money'
                        ? formatAmount(value, i18n.language)
                        : formatNumber(value, i18n.language)
                    }
                    footer={t('reports:totalsFoot', { count: result?.rowCount ?? 0 })}
                  />
                );
              })}
          </div>
        ) : null}

        <Card>
          <CardHeader
            title={t('reports:tableTitle')}
            icon={<BarChart3 className="ico ico-16" aria-hidden="true" />}
            actions={
              result ? (
                <span className="t-12 muted">
                  {t('reports:generatedAt', {
                    count: result.rowCount,
                  })}
                </span>
              ) : null
            }
          />
          <CardBody density="flush">
            {query.isPending ? (
              <TableSkeleton rows={8} columns={5} label={t('common:states.loading')} />
            ) : query.isError ? (
              <ErrorState
                title={t('common:states.errorTitle')}
                description={errorMessage(query.error, t)}
                traceId={errorTraceId(query.error)}
                retryLabel={t('common:actions.retry')}
                onRetry={() => void query.refetch()}
                traceCopyLabel={t('common:actions.copy')}
                traceCopiedLabel={t('common:actions.copied')}
              />
            ) : !result || result.rowCount === 0 ? (
              <EmptyState
                icon={<BarChart3 className="ico ico-24" aria-hidden="true" />}
                title={t('reports:emptyResultTitle')}
                description={t('reports:emptyResultBody')}
              />
            ) : (
              <ReportTable result={result} totalsLabel={t('reports:totals')} />
            )}
          </CardBody>
        </Card>
      </div>
    </Container>
  );
}
